import { useEffect, useState } from "react";
import { Link } from "react-router";
import { TRADE_ORDER_STATE } from "@zhihaoscm/meta";
import request from "@/api";
import "./orderInfo.scss";
import {
    ProductDetailTabSection,
    ProductDetailTabTable,
    ProductDetailTabMetrics,
    emptyProductDetailColumns,
    withProductDetailDateRange,
    type ProductDetailFilterValues,
    type ProductDetailTabProps
} from "@/views/pages/productManagement/ProductDetailTabPrimitives";

export type orderInfoProps = ProductDetailTabProps;

const mobileText = (value: unknown) => {
    const text = String(value || "");
    return text ? text.replace(/(\d{3})(\d{4})(\d{4})/, "$1 $2 $3") : "";
};
const numberText = (value: unknown, money = false) => {
    if (value === null || value === undefined || value === "") return "--";
    const number = Number(value);
    return Number.isFinite(number)
        ? number.toLocaleString("en-US", money ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : undefined)
        : String(value);
};
const dateText = (value: unknown) =>
    String(value || "--")
        .replace("T", " ")
        .slice(0, 16);

/** 寄售订单明细：分别加载吨数与分页数据，保留旧版订单状态和金额的展示规则。 */
export default function MigrationComponent(props: orderInfoProps) {
    const productId = String(props.data?.productConsignment?.id || props.data?.id || "");
    const [ton, setTon] = useState<{ orderedTon: number; pickedUpTon: number }>();
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [filters, setFilters] = useState<ProductDetailFilterValues>({});

    useEffect(() => {
        let disposed = false;
        if (!productId) return;
        setTon(undefined);
        // 两个接口独立更新：吨数请求失败时，不能丢弃已经成功返回的订单列表。
        void request.service
            .get<{ orderedTon: number; pickedUpTon: number }>(`/admin/order/ton`, {
                params: { productId, productType: 1 }
            })
            .then(response => {
                if (!disposed) setTon(response.data || { orderedTon: 0, pickedUpTon: 0 });
            })
            .catch(() => {
                if (!disposed) setTon(undefined);
            });
        return () => {
            disposed = true;
        };
    }, [productId]);

    useEffect(() => {
        let disposed = false;
        if (!productId) return;
        setLoadingOrders(true);
        setRows([]);
        setTotal(0);
        // 与旧 Vue orderInfo 保持相同的商品类型及分页条件，列表不依赖吨数统计接口。
        void request.service
            .get<{ list: any[]; total: number }>(`/admin/order/paging`, {
                params: {
                    page: 1,
                    size: 10,
                    productId,
                    productType: 1,
                    ...withProductDetailDateRange(filters, "date")
                }
            })
            .then(pageResponse => {
                if (disposed) return;
                const list = pageResponse.data?.list || [];
                setTotal(Number(pageResponse.data?.total || list.length));
                setRows(
                    list.map((item: any, index: number) => ({
                        id: item.order?.id || item.id || `order-${index}`,
                        订单编号: item.order?.id ? (
                            <Link to={`/tradeManage/tradeOrder/detail?id=${item.order.id}`}>
                                {item.order.orderId || item.order.id}
                            </Link>
                        ) : (
                            "--"
                        ),
                        采购用户:
                            [
                                item.order?.customerEnterprise?.name || item.order?.customerEnterprise?.realName,
                                mobileText(item.order?.customerEnterprise?.mobile)
                            ]
                                .filter(Boolean)
                                .join(" | ") || "--",
                        "交易单价(元/吨)": numberText(item.order?.unitPrice, true),
                        "采购吨数(吨)": numberText(item.order?.ton),
                        "预估金额(元)": numberText(item.order?.estimatedAmount, true),
                        "实收款(元)": numberText(item.order?.payAmount, true),
                        下单时间: dateText(item.order?.orderTime || item.order?.createdTime),
                        状态:
                            TRADE_ORDER_STATE.getIntroByCode(Number(item.order?.state)) || item.order?.stateName || "--"
                    }))
                );
                setLoadingOrders(false);
            })
            .catch(() => {
                if (!disposed) {
                    setRows([]);
                    setTotal(0);
                    setLoadingOrders(false);
                }
            });
        return () => {
            disposed = true;
        };
    }, [productId, filters]);

    return (
        <ProductDetailTabSection title="订单明细" className="order-info" showTitle={false}>
            <ProductDetailTabMetrics
                items={[
                    { label: "已售吨数（吨）", value: ton?.orderedTon?.toLocaleString() ?? "--", type: "num" },
                    { label: "已提货吨数（吨）", value: ton?.pickedUpTon?.toLocaleString() ?? "--", type: "num" }
                ]}
            />
            <ProductDetailTabTable
                ariaLabel="订单明细"
                columns={emptyProductDetailColumns([
                    "订单编号",
                    "采购用户",
                    "交易单价(元/吨)",
                    "采购吨数(吨)",
                    "预估金额(元)",
                    "实收款(元)",
                    "下单时间",
                    "状态"
                ])}
                data={rows}
                loading={loadingOrders}
                total={total}
                onSearch={setFilters}
            />
            {props.children}
        </ProductDetailTabSection>
    );
}
